import { DrawerLink } from '../../app/DrawerLink';
import { usePlayer } from '../../stores/player';
import type { Message } from './api';

/**
 * A track somebody sent you, playable where it sits.
 *
 * ## Why it plays rather than links
 *
 * §6.5 calls sharing a track into a message the single highest-intent discovery
 * path on the platform - a friend sending you a song converts better than any
 * ranking does - and the way to lose that is to make the person go somewhere
 * else to hear it. So the artwork and the title link to the track, and the
 * button plays it without leaving the conversation.
 *
 * ## One card, two places
 *
 * The full messages page and the docked chat rail both show threads, and this
 * used to exist only in the first. A track shared into a conversation somebody
 * had open in the dock rendered as an empty bubble - the body is null when the
 * message is only an attachment, so there was nothing at all to see.
 *
 * `compact` is the dock: the same card, sized for a rail a third the width.
 */
export function SharedTrack({
  track,
  mine,
  compact = false,
}: {
  track: NonNullable<Message['track']>;
  /** Sent by the viewer, which decides what the card sits on. */
  mine: boolean;
  compact?: boolean;
}) {
  const play = usePlayer((state) => state.play);

  const size = compact ? 'h-7 w-7' : 'h-9 w-9';

  return (
    <div
      className={`mt-2 flex items-center gap-2 rounded-[var(--radius-control)] p-2 ${
        /*
         * A wash over whatever it is on, rather than a colour of its own: the
         * bubble is the accent when you sent it and a raised surface when you
         * did not, and a card with one fixed background is unreadable on one of
         * the two.
         */
        mine ? 'bg-black/20' : 'bg-app'
      }`}
      data-shared-track={track.id}
    >
      <button
        type="button"
        aria-label={`Play ${track.title}`}
        onClick={() => void play(track, [track], 'feed')}
        className={`flex ${size} shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink transition-colors hover:bg-accent-hover`}
      >
        <svg
          viewBox="0 0 16 16"
          className={`${compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} translate-x-px`}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
        </svg>
      </button>

      {track.artwork_small && (
        <img
          src={track.artwork_small}
          alt=""
          loading="lazy"
          className={`${size} shrink-0 rounded-[var(--radius-control)] object-cover`}
        />
      )}

      <DrawerLink to={`/tracks/${track.id}`} className="min-w-0 flex-1 leading-tight">
        <span className={`block truncate font-medium ${compact ? 'text-[11px]' : 'text-xs'}`}>
          {track.title}
        </span>
        {track.owner && (
          <span className={`block truncate opacity-70 ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
            @{track.owner.username}
          </span>
        )}
      </DrawerLink>
    </div>
  );
}
